import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import languageSelectorIcon from '../../assets/icons/language-selector.svg';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '' }) => {
  const { 
    currentLanguage, 
    currentLanguageName, 
    supportedLanguages, 
    changeLanguage, 
    isRTL,
    isLoading 
  } = useTranslation();
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = async (languageCode: string) => {
    if (languageCode !== currentLanguage && !isLoading) {
      await changeLanguage(languageCode);
      // Recargar la página para aplicar todos los cambios
      window.location.reload();
    }
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!isLoading) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`language-selector ${className}`} ref={dropdownRef}>
      <button 
        className="language-selector-button"
        onClick={toggleDropdown}
        disabled={isLoading}
        aria-label={`Change language (current: ${currentLanguageName})`}
      >
        <img 
          src={languageSelectorIcon} 
          alt="Language selector" 
          className="language-icon"
        />
        <span className="language-text">{currentLanguageName}</span>
      </button>
      
      {isOpen && (
        <div className={`language-dropdown ${isRTL ? 'rtl' : ''}`}>
          {Object.entries(supportedLanguages).map(([code, name]) => (
            <button
              key={code}
              className={`language-option ${code === currentLanguage ? 'active' : ''}`}
              onClick={() => handleLanguageChange(code)}
              disabled={isLoading}
            >
              <span className="language-code">{code.toUpperCase()}</span>
              <span className="language-name">{name}</span>
              {code === currentLanguage && (
                <span className="checkmark">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
